import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, styled, Table, TableBody } from '@mui/material'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import Link from '~/components/common/link/Link'
import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import { fetchQuestionsForSoap } from '~/store/actions/questions'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getSoapQuestionsBySoapId,
  getSoapQuestionsWithWorkFlow,
} from '~/store/reducers/questions'
import { getSoapId } from '~/store/reducers/soap'
import { hasSelectedAnswers } from '~/utils/questionUtils'
import useWorkflowTypes from '~/utils/useWorkflowTypes'

import QuestionsListWidgetLockedFollowUpItem from './QuestionsListWidgetLockedFollowUpItem'
import QuestionsListWidgetLockedItem from './QuestionsListWidgetLockedItem'

const StyledRootGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.colors.grayWhite,
  border: theme.constants.tableBorder,
}))

const StyleTitleGrid = styled(Grid)(({ theme }) => ({
  borderRight: theme.constants.tabBorder,
}))

const StyleWorkflowGrid = styled(Grid)(({ theme }) => ({
  '&:not(:first-of-type)': {
    marginTop: theme.spacing(3),
  },
}))

const StyledTable = styled(Table)(({ theme }) => ({
  border: theme.constants.tabBorder,
  borderRadius: 2,
}))

const StyledH2 = styled(Typography.H2)(({ theme }) => ({
  color: theme.colors.brandPrimary,
}))

const StyledH3 = styled(Typography.H3)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.colors.brandSecondary,
}))

const QuestionsListWidgetLocked = () => {
  const dispatch = useDispatch()
  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )
  const soapId = useSelector(getSoapId)
  const soapQuestionsWithWorkflow = useSelector(getSoapQuestionsWithWorkFlow)
  const soapQuestionsBySoapId = useSelector(getSoapQuestionsBySoapId)
  const WorkFlowTypes = useWorkflowTypes()
  const { t } = useTranslation(['Common', 'Soap'])
  const LockedItemComponent = isFollowUpQuestionsEnabled
    ? QuestionsListWidgetLockedFollowUpItem
    : QuestionsListWidgetLockedItem

  useEffect(() => {
    if (soapId) {
      dispatch(fetchQuestionsForSoap(soapId))
    }
  }, [soapId])

  const questionsByWorkflowType = WorkFlowTypes.map((type) => ({
    type,
    questions: soapQuestionsBySoapId
      .filter((question) => question.workflowIds.indexOf(type.id) > -1) // matches type
      .filter((question) => question.answers?.some(({ selected }) => selected)), // has answers
  })).filter(({ questions }) => questions.length > 0)

  return (
    <StyledRootGrid container item wrap="nowrap">
      <StyleTitleGrid item px={1.5} py={2} xs={2}>
        <StyledH2>{t('Common:QUESTION_OTHER')}</StyledH2>
        {WorkFlowTypes.map((workflow) => {
          const questionsOfType = soapQuestionsWithWorkflow[workflow.id]
          const answeredQuestionsOfType = questionsOfType.filter(
            ({ answers }) => hasSelectedAnswers(answers),
          )
          const answeredQuestionsStr = t(
            'Soap:QUESTIONS_LIST_LOCKED.X_OF_Y_QUESTIONS_ANSWERED',
            {
              x: answeredQuestionsOfType.length,
              y: questionsOfType.length,
            },
          )

          if (questionsOfType.length === 0) {
            return null
          }

          return (
            <Typography.Paragraph key={workflow.id}>
              {`${LanguageUtils.getTranslatedFieldName(
                workflow,
                'shortName',
              )}: ${answeredQuestionsStr}`}
            </Typography.Paragraph>
          )
        })}
      </StyleTitleGrid>
      <Grid container item xs direction="column" pb={3} pt={1.5} px={3}>
        {questionsByWorkflowType.length > 0 ? (
          questionsByWorkflowType.map(({ type, questions }) => (
            <StyleWorkflowGrid container item key={type.id}>
              <StyledH3>
                {LanguageUtils.getTranslatedFieldName(type, 'shortName')}
              </StyledH3>
              <StyledTable>
                <TableBody>
                  {questions.map((question) => (
                    <LockedItemComponent
                      key={question.id}
                      question={question}
                    />
                  ))}
                </TableBody>
              </StyledTable>
            </StyleWorkflowGrid>
          ))
        ) : (
          <Typography.ParagraphLg>
            <Trans
              components={{ linkTo: <Link to="/admin/catalog/questions" /> }}
              i18nKey="Soap:QUESTIONS_LIST_LOCKED.NO_QUESTIONS"
            />
          </Typography.ParagraphLg>
        )}
      </Grid>
    </StyledRootGrid>
  )
}

export default QuestionsListWidgetLocked
